import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";

interface PreviewDeployment {
  branch: string;
  branchSlug: string;
  previewId: string;
  commitSha: string;
  author: string;
  deployedAt: string;
  url: string;
}

interface PreviewDeploymentVersionProps {
  position?: "left" | "right";
  showOnDevelop?: boolean;
  developUrl?: string;
}

type DeploymentContext = "develop" | "preview" | "none";

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`;

const VersionContainer = styled.div<{ position: "left" | "right" }>`
  position: fixed;
  ${(props) => (props.position === "left" ? "left: 0;" : "right: 0;")}
  top: 50%;
  transform: translateY(-50%)
    ${(props) =>
      props.position === "left" ? "rotate(-90deg)" : "rotate(90deg)"};
  transform-origin: ${(props) =>
    props.position === "left" ? "left bottom" : "right top"};
  z-index: 9999;
  pointer-events: auto;
`;

const LabelButton = styled.button<{ $hasUpdate: boolean }>`
  background: ${(props) => (props.$hasUpdate ? "white" : "transparent")};
  border: none;
  border-radius: ${(props) => (props.$hasUpdate ? "3px" : "0")};
  color: ${(props) =>
    props.$hasUpdate ? "rgba(255, 0, 0, 0.8)" : "rgba(0, 0, 0, 0.3)"};
  font-size: 10px;
  font-family: monospace;
  font-weight: ${(props) => (props.$hasUpdate ? "bold" : "normal")};
  cursor: pointer;
  padding: 4px 8px;
  outline: none;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;

  &:hover {
    color: ${(props) =>
      props.$hasUpdate ? "rgba(255, 0, 0, 1)" : "rgba(0, 0, 0, 0.6)"};
  }
`;

const PulsingDot = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: red;
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

const Popover = styled.div<{ position: "left" | "right" }>`
  position: fixed;
  ${(props) => (props.position === "left" ? "left: 24px;" : "right: 24px;")}
  top: 50%;
  transform: translateY(-50%);
  z-index: 10000;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  min-width: 280px;
  max-width: 360px;
  max-height: 400px;
  overflow-y: auto;
  font-family: monospace;
  font-size: 11px;
`;

const Section = styled.div`
  padding: 8px 0;
  &:not(:last-child) {
    border-bottom: 1px solid #eee;
  }
`;

const SectionLabel = styled.div`
  padding: 2px 12px 4px;
  font-size: 9px;
  text-transform: uppercase;
  color: #888;
  letter-spacing: 0.5px;
`;

const DeploymentItem = styled.button<{ $isCurrent: boolean }>`
  display: block;
  width: 100%;
  padding: 6px 12px;
  border: none;
  background: ${(props) => (props.$isCurrent ? "#f0f4ff" : "transparent")};
  text-align: left;
  cursor: pointer;
  font-family: monospace;
  font-size: 11px;
  color: #333;
  line-height: 1.4;

  &:hover {
    background: ${(props) => (props.$isCurrent ? "#e0e8ff" : "#f5f5f5")};
  }
`;

const ItemLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const ItemMeta = styled.span`
  color: #999;
  font-size: 10px;
`;

const CurrentMarker = styled.span`
  color: #4a7dff;
  font-size: 10px;
`;

function parseManifest(content: string): PreviewDeployment[] {
  const lines = content.split("\n");
  const deployments: PreviewDeployment[] = [];

  for (const line of lines) {
    if (!line.trim() || line.startsWith("#")) {
      continue;
    }

    const parts = line.split("|");
    if (parts.length >= 7) {
      deployments.push({
        branch: parts[0],
        branchSlug: parts[1],
        previewId: parts[2],
        commitSha: parts[3],
        author: parts[4],
        deployedAt: parts[5],
        url: parts[6]
      });
    } else if (parts.length >= 6) {
      // Old 6-field format (transition compatibility)
      deployments.push({
        branch: parts[0],
        branchSlug: "",
        previewId: parts[1],
        commitSha: parts[2],
        author: parts[3],
        deployedAt: parts[4],
        url: parts[5]
      });
    }
  }

  return deployments;
}

function extractPreviewInfo(): {
  slug: string;
  hash: string | null;
  isLatest: boolean;
} | null {
  const previewMatch = window.location.pathname.match(/\/preview-([^/]+)\/?/);
  if (!previewMatch) {
    return null;
  }
  const [, segment] = previewMatch;

  // Legacy: /preview-<slug>-latest/ — the branch pointer before the suffix was
  // dropped. Folders in this shape survive in S3 until the 60-day sweep, and
  // manifest/Jira links still point at them, so keep reading them.
  const legacyLatest = segment.match(/^(.+)-latest$/);
  if (legacyLatest) {
    return { slug: legacyLatest[1], hash: null, isLatest: true };
  }

  // Legacy: /preview-<slug>-<hash>/ — one folder per commit.
  const legacyCommit = segment.match(/^(.+)-([a-f0-9]{8})$/);
  if (legacyCommit) {
    return { slug: legacyCommit[1], hash: legacyCommit[2], isLatest: false };
  }

  // Legacy: /preview-<hash>/ — no slug at all.
  if (/^[a-f0-9]{8}$/.test(segment)) {
    return { slug: "", hash: segment, isLatest: false };
  }

  // Current: /preview-<slug>/ — one folder per branch or stack, which always
  // holds the newest build, so it is latest by construction.
  return { slug: segment, hash: null, isLatest: true };
}

function getAppRelativePath(stripParams?: string[]): string {
  const path = window.location.pathname;
  const hash = window.location.hash;

  // Strip preview prefix: /preview-<anything>/ -> /
  const stripped = path.replace(/^\/preview-[^/]+\/?/, "/");

  let search = window.location.search;
  if (stripParams?.length) {
    const params = new URLSearchParams(window.location.search);
    for (const p of stripParams) {
      params.delete(p);
    }
    search = params.toString() ? `?${params.toString()}` : "";
  }

  return `${stripped}${search}${hash}`;
}

function buildUrlWithPath(baseUrl: string, appPath: string): string {
  const url = new URL(baseUrl);
  const basePath = url.pathname.replace(/\/$/, "");
  const normalizedAppPath = appPath.startsWith("/") ? appPath : `/${appPath}`;
  url.pathname =
    normalizedAppPath === "/"
      ? `${basePath}/`
      : `${basePath}${normalizedAppPath}`;
  return url.toString();
}

function formatDate(dateStr: string): string {
  try {
    const date = new Date(dateStr.replace(" UTC", "Z"));
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return dateStr;
  }
}

function getDeploymentContext(
  previewInfo: ReturnType<typeof extractPreviewInfo>,
  showOnDevelop: boolean
): DeploymentContext {
  if (previewInfo) {
    return "preview";
  }
  if (showOnDevelop) {
    return "develop";
  }
  return "none";
}

function groupDeploymentsByBranch(
  deployments: PreviewDeployment[]
): Map<string, PreviewDeployment[]> {
  const groups = new Map<string, PreviewDeployment[]>();

  for (const deployment of deployments) {
    const existing = groups.get(deployment.branch) || [];
    existing.push(deployment);
    groups.set(deployment.branch, existing);
  }

  for (const [, deps] of groups) {
    deps.sort(
      (a, b) =>
        new Date(b.deployedAt).getTime() - new Date(a.deployedAt).getTime()
    );
  }

  return groups;
}

function PreviewDeploymentVersion({
  position = "right",
  showOnDevelop = false,
  developUrl
}: PreviewDeploymentVersionProps) {
  const [deployments, setDeployments] = useState<PreviewDeployment[]>([]);
  const [previewInfo, setPreviewInfo] = useState<ReturnType<
    typeof extractPreviewInfo
  > | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const context = useMemo(() => {
    return getDeploymentContext(previewInfo, showOnDevelop);
  }, [previewInfo, showOnDevelop]);

  const fetchManifest = useCallback(() => {
    const manifestUrl = `${window.location.origin}/preview-manifest.txt?t=${Date.now()}`;

    fetch(manifestUrl, { cache: "no-store" })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return res.text();
      })
      .then((content) => {
        const parsed = parseManifest(content);
        setDeployments(parsed);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("An unknown error occurred");
        }
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const info = extractPreviewInfo();
    setPreviewInfo(info);

    const deploymentContext = getDeploymentContext(info, showOnDevelop);

    if (deploymentContext === "none") {
      setLoading(false);
      return;
    }

    fetchManifest();

    const interval = setInterval(fetchManifest, 10_000);
    return () => clearInterval(interval);
  }, [showOnDevelop, fetchManifest]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleClick = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [isOpen]);

  const developDeployment = useMemo(() => {
    return deployments.find((d) => d.branch === "develop") ?? null;
  }, [deployments]);

  const currentDeployment = useMemo(() => {
    if (previewInfo?.isLatest && previewInfo.slug) {
      return (
        deployments
          .filter((d) => d.branchSlug === previewInfo.slug)
          .sort(
            (a, b) =>
              new Date(b.deployedAt).getTime() -
              new Date(a.deployedAt).getTime()
          )[0] ?? null
      );
    }
    if (!previewInfo?.hash) {
      return null;
    }
    return deployments.find((d) => d.previewId === previewInfo.hash) ?? null;
  }, [deployments, previewInfo]);

  const branchDeployments = useMemo(() => {
    if (!currentDeployment) {
      return [];
    }
    return deployments
      .filter((d) => d.branch === currentDeployment.branch)
      .sort(
        (a, b) =>
          new Date(b.deployedAt).getTime() - new Date(a.deployedAt).getTime()
      );
  }, [deployments, currentDeployment]);

  const groupedDeployments = useMemo(() => {
    const nonDevelopDeployments = deployments.filter(
      (d) => d.branch !== "develop"
    );
    return groupDeploymentsByBranch(nonDevelopDeployments);
  }, [deployments]);

  const latestPerBranch = useMemo(() => {
    const latest: PreviewDeployment[] = [];
    for (const [, deps] of groupedDeployments) {
      if (deps.length > 0) {
        latest.push(deps[0]);
      }
    }
    return latest.sort(
      (a, b) =>
        new Date(b.deployedAt).getTime() - new Date(a.deployedAt).getTime()
    );
  }, [groupedDeployments]);

  const isLatest = useMemo(() => {
    if (context === "develop") {
      return true;
    }
    if (previewInfo?.isLatest) {
      return true;
    }
    if (!currentDeployment || branchDeployments.length === 0) {
      return true;
    }
    return branchDeployments[0].previewId === previewInfo?.hash;
  }, [context, branchDeployments, previewInfo, currentDeployment]);

  // Auto-upgrade (skip for -latest URLs — they always serve the latest build)
  useEffect(() => {
    if (previewInfo?.isLatest) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get("auto-preview-upgrade") !== "true") {
      return;
    }
    if (context !== "preview" || loading || branchDeployments.length === 0) {
      return;
    }

    const appPath = getAppRelativePath(["auto-preview-upgrade"]);

    if (!isLatest) {
      const newestUrl = buildUrlWithPath(branchDeployments[0].url, appPath);
      window.location.replace(newestUrl);
    } else {
      const cleanUrl = buildUrlWithPath(
        currentDeployment?.url || window.location.origin,
        appPath
      );
      window.history.replaceState(null, "", cleanUrl);
    }
  }, [
    context,
    loading,
    isLatest,
    branchDeployments,
    currentDeployment,
    previewInfo
  ]);

  if (context === "none") {
    return null;
  }
  if (loading) {
    return null;
  }
  if (error) {
    return null;
  }

  const navigateTo = (url: string) => {
    const targetUrl = buildUrlWithPath(url, getAppRelativePath());
    if (targetUrl !== window.location.href) {
      window.location.href = targetUrl;
    }
    setIsOpen(false);
  };

  const truncateBranch = (branch: string, maxLength = 25) =>
    branch.length > maxLength ? `${branch.slice(0, maxLength)}...` : branch;

  // Build label text
  const getLabelText = () => {
    if (context === "develop") {
      return developDeployment
        ? `develop @ ${formatDate(developDeployment.deployedAt)}`
        : "develop";
    }
    if (currentDeployment) {
      return `${truncateBranch(currentDeployment.branch, 18)} ${currentDeployment.commitSha.slice(0, 8)} ${formatDate(currentDeployment.deployedAt)}`;
    }
    return `preview-${previewInfo?.hash || "unknown"}`;
  };

  const hasUpdate = !isLatest;

  // Develop context render
  if (context === "develop") {
    return (
      <VersionContainer position={position}>
        <LabelButton
          ref={buttonRef}
          $hasUpdate={false}
          onClick={() => setIsOpen(!isOpen)}
          title="Switch to a preview deployment"
        >
          {getLabelText()}
        </LabelButton>
        {isOpen && (
          <Popover ref={popoverRef} position={position}>
            <Section>
              <SectionLabel>Current</SectionLabel>
              <DeploymentItem
                $isCurrent={true}
                onClick={() => setIsOpen(false)}
              >
                <ItemLine>
                  <span>develop</span>
                  {developDeployment && (
                    <ItemMeta>
                      {formatDate(developDeployment.deployedAt)}
                    </ItemMeta>
                  )}
                </ItemLine>
              </DeploymentItem>
            </Section>
            {latestPerBranch.length > 0 && (
              <Section>
                <SectionLabel>Preview deployments</SectionLabel>
                {latestPerBranch.map((d) => (
                  <DeploymentItem
                    key={d.previewId}
                    $isCurrent={false}
                    onClick={() => navigateTo(d.url)}
                  >
                    <ItemLine>
                      <span>{truncateBranch(d.branch)}</span>
                      <ItemMeta>{formatDate(d.deployedAt)}</ItemMeta>
                    </ItemLine>
                  </DeploymentItem>
                ))}
              </Section>
            )}
          </Popover>
        )}
      </VersionContainer>
    );
  }

  // Preview context render
  const developOptionUrl = developUrl || developDeployment?.url;

  return (
    <VersionContainer position={position}>
      <LabelButton
        ref={buttonRef}
        $hasUpdate={hasUpdate}
        onClick={() => setIsOpen(!isOpen)}
        title={
          hasUpdate
            ? "Update available - click to switch"
            : "Latest preview for this branch"
        }
      >
        {hasUpdate && <PulsingDot />}
        {getLabelText()}
      </LabelButton>
      {isOpen && (
        <Popover ref={popoverRef} position={position}>
          {branchDeployments.length > 0 && (
            <Section>
              <SectionLabel>
                {truncateBranch(currentDeployment?.branch || "")}
                {hasUpdate ? " - update available" : ""}
              </SectionLabel>
              {branchDeployments.map((d) => {
                const isCurrent = d.previewId === previewInfo?.hash;
                return (
                  <DeploymentItem
                    key={d.previewId}
                    $isCurrent={isCurrent}
                    onClick={() => navigateTo(d.url)}
                  >
                    <ItemLine>
                      <span>{d.commitSha.slice(0, 8)}</span>
                      <ItemMeta>
                        {formatDate(d.deployedAt)}
                        {isCurrent && <CurrentMarker> current</CurrentMarker>}
                      </ItemMeta>
                    </ItemLine>
                  </DeploymentItem>
                );
              })}
            </Section>
          )}
          <Section>
            <SectionLabel>Switch to</SectionLabel>
            {developOptionUrl && (
              <DeploymentItem
                $isCurrent={false}
                onClick={() => navigateTo(developOptionUrl)}
              >
                <ItemLine>
                  <span>develop</span>
                  {developDeployment && (
                    <ItemMeta>
                      {formatDate(developDeployment.deployedAt)}
                    </ItemMeta>
                  )}
                </ItemLine>
              </DeploymentItem>
            )}
            {latestPerBranch
              .filter((d) => d.branch !== currentDeployment?.branch)
              .map((d) => (
                <DeploymentItem
                  key={d.previewId}
                  $isCurrent={false}
                  onClick={() => navigateTo(d.url)}
                >
                  <ItemLine>
                    <span>{truncateBranch(d.branch)}</span>
                    <ItemMeta>{formatDate(d.deployedAt)}</ItemMeta>
                  </ItemLine>
                </DeploymentItem>
              ))}
          </Section>
        </Popover>
      )}
    </VersionContainer>
  );
}

export default PreviewDeploymentVersion;
